import { createContext, useEffect, useRef, useState } from 'react';
import { getChatFromDB, saveChatToDB, saveCompletedForm, sendLeadToRedTrack } from '../Utils/api';
import { useUserTracking } from '../Hooks/useUserTracking';
import { /*getCalendlyLinkByIncome, */getGHLCalendar, redirect, readCampaignParams, hasCampaignParams, readMetaCookies } from '../Utils/functions';
import {
  ACTIVE_RULESET,
  LANE_BELOW_FLOOR,
  LANE_BORDERLINE,
  RULESET_THREE_LANE,
  resolveIncomeLane,
  routesAsQualified,
} from '../Utils/incomeLane';
import steps from '../Utils/constants';

const { VITE_HOME_PAGE, VITE_THANK_YOU_PAGE } = import.meta.env;

// The free-database lanes no longer redirect out on submit: below_floor and the borderline decliner
// land on an in-funnel confirmation screen (step 16, FREE_DATABASE_CONFIRMATION), whose own button
// sends the visitor to the VIDA Select home. So no per-lane destination variable is involved.

// Whether the three-lane routing is live. The kill switch changes which lane a visitor gets, but
// it cannot by itself gate a destination that exists in only one of the two worlds: the rejection
// screens are retired under three lanes and still in use under the legacy rules. Deploy 1 ships
// with the legacy ruleset and screen 15 must be unreachable, so that one guard is gated here.
const THREE_LANE_ROUTING = ACTIVE_RULESET === RULESET_THREE_LANE;
const {
  AGE_RANGE_OF_LOOKING_FOR,
  AGREED_TO_BUNDLE_GET_EMAIL,
  CALENDLY_PAGE,
  CURRENT_INCOME_LEVEL,
  DATING_OBJECTIVE,
  LOW_INCOME_AGREEMENT,
  NEWSLETTER_SIGNUP,
  PHONE_OR_VIDEO,
  READY_TO_SPEAK_GET_EMAIL,
  FREE_DATABASE_OPTIN,
  FREE_DATABASE_CONFIRMATION,
} = steps;

const initialState = {
  step: 0,
  visitedSteps: [],
  lookingFor: '',
  gender: '',
  name: '',
  relationStatus: '',
  address: '',
  age: '',
  fromAge: '',
  toAge: '',
  matchEarn: '',
  income: '',
  datingObjective: '',
  readyToSpeak: '',
  email: '',
  lowIncomeDisclaimerAgreement: '',
  newsletterSignup: '',
  preferToMeetBy: '',
  calendlyLink: '',
  adjustIncome: false,
  phoneLink: '',
  videoLink: '',
  params: null,
};

export const StepsContext = createContext(initialState);

/* Redirect occurs after Calendly booking page to v1.0 */
export const StepsContextProvider = ({ children }) => {
  // Seed the campaign params from the URL once, at initialization, instead of with a setState during
  // render (removed from Steps). This reads the current click's attribution deterministically before
  // the saved session loads, so the restore effect below can decide the winner by rule, not by timing.
  const [state, setState] = useState(() => ({ ...initialState, params: { ...readCampaignParams(), ...readMetaCookies() } }));
  const [shouldSave, setShouldSave] = useState(false);
  const [error, setError] = useState(null);
  const [stateLoading, setStateLoading] = useState(true);
  const [leadType, setLeadType] = useState(null); // 'Prime', 'Foreign' or 'Junk'
  const [fakeLoading, setFakeLoading] = useState(false); // used to mimic server response processing
  const [done, setDone] = useState(false); // used to indicate form completion

  const { ipAddress, GAId, fingerprintId } = useUserTracking();

  const {
    step,
    visitedSteps,
    lookingFor,
    gender,
    income,
    email,
    lowIncomeDisclaimerAgreement,
    newsletterSignup,
    preferToMeetBy,
    calendlyLink,
    adjustIncome,
    phoneLink,
    videoLink,
  } = state;

  // Derived on every render from two values that are already persisted, so it cannot go stale and
  // needs no synchronisation effect. Declared here rather than beside the other predicates below
  // because the calendar-preload effect reads it.
  const incomeLane = resolveIncomeLane(gender, income);

  // Get state data from db
  useEffect(() => {
    const getState = async () => {
      setStateLoading(true);

      const data = await getChatFromDB();
      // Fresh visit (no saved session): keep the URL params already seeded into state at init.
      if (!data) return setStateLoading(false);

      // Returning visit — decide the surviving campaign params by last-touch (Cristian, 2026-09-29;
      // JM to confirm). A fresh ad click on top of an old session wins; a param-less return (direct or
      // organic) keeps the session's existing attribution rather than wiping it. This replaces the
      // blind `setState(data.state)` whose params depended on which write finished last.
      const urlParams = readCampaignParams();
      // Meta cookies ride along on both branches without touching the last-touch decision (which stays
      // URL-driven): a fresh ad click takes the new URL params plus the current Meta cookies; a
      // param-less return keeps the session's attribution, which already carries the cookies it was
      // seeded with. readMetaCookies() omits absent cookies, so this never nulls a captured value.
      const params = hasCampaignParams(urlParams)
        ? { ...urlParams, ...readMetaCookies() }
        : (data.state?.params ?? null);

      setState({ ...data.state, params });
      setLeadType(data.leadType);
      setStateLoading(false);
    };

    getState();
  }, []);

  // store previous income in ref to be able to detect income change
  const prevIncome = useRef(null);

  // determine calendly links beforehand to preload iframes and cache the assets
  // we don't know does client prefer phone or video, so preload both options for income
  useEffect(() => {
    // The below-floor check is mandatory, not an optimisation. This effect fires as soon as an
    // income exists, at step 7, before the funnel can divert anyone: without it a visitor who
    // will never book already has a seller assigned and both booking iframes loaded with their
    // name and email.
    if (leadType !== 'Junk' && incomeLane !== LANE_BELOW_FLOOR && income && !phoneLink && !videoLink) {
      prevIncome.current = income;
      //const { man_phone, man_video, woman_phone, woman_video } = getCalendlyLinkByIncome(income, adjustIncome);

      const phoneCalendar = getGHLCalendar(
        state.income,
        state.adjustIncome,
        'phone'
      );

      const videoCalendar = getGHLCalendar(
        state.income,
        state.adjustIncome,
        'video'
      );

      const newPhoneLink = phoneCalendar.calendarId;
      const newVideoLink = videoCalendar.calendarId;


      //const newPhoneLink = gender === 'I am a man' ? man_phone : woman_phone;
      //const newVideoLink = gender === 'I am a man' ? man_video : woman_video;

      setState((state) => ({ ...state, phoneLink: newPhoneLink, videoLink: newVideoLink }));
    }
  }, [leadType, income, gender, adjustIncome, phoneLink, videoLink, incomeLane]);

  // set links to null if user changes income
  useEffect(() => {
    if (!income) return;

    if (income !== prevIncome.current) {
      setState((state) => ({ ...state, phoneLink: null, videoLink: null }));
    }
  }, [income]);

  // ref value persists between rerenders
  // used to indicate that request to '/completed' endpoint has been fired
  // and should not be refetched after rerender caused by calendly link setState()
  const isRequestToOpFired = useRef(null);

  // Send completed form data
  useEffect(() => {
    const saveData = async () => {
      if (calendlyLink) {
        isRequestToOpFired.current = true;

        await saveChatToDB({ ...state, step: CALENDLY_PAGE }); // save data to db
        await saveCompletedForm(); // save lead to Ontraport

        // if name does not include 'test'
        if (!state.name.toLowerCase().includes('test')) {
          await sendLeadToRedTrack(state.params?.clickId); // send clickId to Red Track analytics
        }

        setState((state) => ({ ...state, step: CALENDLY_PAGE }));
        setFakeLoading(false);
      }
    };

    // if form is completed and request does not yet fired
    if (done && !isRequestToOpFired.current) saveData();
  }, [done, step, state, calendlyLink]);

  const genderManOrLookingForWomen = gender === 'I am a man' || lookingFor === 'I am looking to meet single women';
  const agreedToDisclaimer = lowIncomeDisclaimerAgreement === 'yes';
  const agreedToNewsletter = newsletterSignup === 'yes';

  // used to indicate whether user already visited the step page
  const stepPassed = visitedSteps.includes(step);

  // get animation class with passed duration
  const animate = (duration) => `animate-${duration}`;

  // imitate server response by delaying steps movement
  const fakeLoadingDuration = 500;

  const makeFakeLoading = () => {
    setFakeLoading(true);

    setTimeout(() => {
      setFakeLoading(false);
    }, fakeLoadingDuration);
  };

  // set new state and jump to another step
  const saveAndGoToStep = async (step) => {
    setState((state) => ({ ...state, step }));
    setFakeLoading(false);
    if (shouldSave) await saveChatToDB({ ...state, step });
  };

  const saveAndRedirect = async (url) => {
    await saveChatToDB({ ...state });
    redirect(url);
  };

  const next = async () => {
    setFakeLoading(true);
    setError(null); // drop error state on step forward

    // set visited steps with filtered duplicates
    const newVisitedSteps = [...new Set([...visitedSteps, step])];
    setState((state) => ({ ...state, visitedSteps: newVisitedSteps }));

    // skip "MatchEarn" if looking for Women or gender is Man
    if (step === AGE_RANGE_OF_LOOKING_FOR && genderManOrLookingForWomen) {
      return saveAndGoToStep(CURRENT_INCOME_LEVEL);
    }

    // Below the floor: leave the sales funnel for the free member network.
    // Inert under the legacy ruleset, whose table never returns below_floor.
    if (step === DATING_OBJECTIVE && incomeLane === LANE_BELOW_FLOOR) {
      return saveAndGoToStep(FREE_DATABASE_OPTIN);
    }

    // Qualified, and unclassified by design: straight to email capture and booking.
    // Written as a negation of borderline rather than as an if/else chain so that borderline falls
    // through to the default at the end of this function. That is deliberate: saveAndGoToStep sends
    // only the state, while the default path also refreshes leadType and adjustIncome, which is the
    // save behaviour that population has today.
    if (step === DATING_OBJECTIVE && incomeLane !== LANE_BORDERLINE) {
      return saveAndGoToStep(READY_TO_SPEAK_GET_EMAIL);
    }

    // A session parked on the price screen whose lane changed when the ruleset was flipped. Without
    // this it matches no guard below, falls through to the default and lands on step 10, a screen
    // retired from the funnel. Inert under the legacy ruleset.
    if (step === LOW_INCOME_AGREEMENT && incomeLane === LANE_BELOW_FLOOR) {
      return saveAndGoToStep(FREE_DATABASE_OPTIN);
    }

    // jump to "Subcribe to newsletter" - ORDER MATTERS: this guard has always been evaluated before
    // the one below it, so a visitor who set the newsletter answer, went back to this screen and
    // pressed Next again reaches step 11 rather than step 12. Reordering the two would be a silent
    // behaviour change under the legacy ruleset. Unreachable under three lanes, where nothing routes
    // into the newsletter screen in the first place.
    if (step === LOW_INCOME_AGREEMENT && incomeLane === LANE_BORDERLINE && agreedToNewsletter && agreedToDisclaimer) {
      return saveAndGoToStep(AGREED_TO_BUNDLE_GET_EMAIL);
    }

    // Accepted the price: on to email capture and booking
    if (step === LOW_INCOME_AGREEMENT && incomeLane === LANE_BORDERLINE && agreedToDisclaimer) {
      return saveAndGoToStep(READY_TO_SPEAK_GET_EMAIL);
    }

    // Declined the price: the free member network. Gated on the ruleset because this destination
    // only exists under three lanes - under the legacy rules the visitor must still fall through to
    // the newsletter screen, which is what keeps deploy 1 identical to today.
    if (THREE_LANE_ROUTING && step === LOW_INCOME_AGREEMENT && incomeLane === LANE_BORDERLINE) {
      return saveAndGoToStep(FREE_DATABASE_OPTIN);
    }

    // jump from "Subscribe to newsletter" to "Phone or Video"
    if (step === AGREED_TO_BUNDLE_GET_EMAIL && agreedToDisclaimer && email) {
      return saveAndGoToStep(PHONE_OR_VIDEO);
    }

    // Exit from chat on low income and email subscription refuse
    if (step === NEWSLETTER_SIGNUP && newsletterSignup === 'no') {
      return saveAndRedirect(VITE_HOME_PAGE);
    }

    // Exit from chat on low income and email subscription agree
    if (step === AGREED_TO_BUNDLE_GET_EMAIL && agreedToNewsletter && email) {
      await saveChatToDB({ ...state });
      if (leadType !== 'Junk') await saveCompletedForm();
      return redirect(VITE_THANK_YOU_PAGE);
    }

    // redirect from calendly page if lead type is junk
    if (step === PHONE_OR_VIDEO && leadType === 'Junk') {
      return saveAndRedirect(VITE_THANK_YOU_PAGE);
    }

    // Exit from the free member network screen
    if (step === FREE_DATABASE_OPTIN && email) {
      // Four arguments, not one: saveAndGoToStep and saveAndRedirect pass only the state, and were
      // this ever the first save the POST would go out without a fingerprint, fail validation and
      // lose the session silently.
      await saveChatToDB({ ...state }, ipAddress, GAId, fingerprintId);

      // Truthy AND not 'Junk': the endpoint also refuses a missing leadType with a 403, and the API
      // layer reacts to a 403 by clearing the token and navigating away, which would strand the
      // visitor with no record and no message.
      if (leadType && leadType !== 'Junk') {
        try {
          await saveCompletedForm();
        } catch {
          // never block the exit: saveCompletedForm dereferences err.response.status with no guard,
          // so a network failure throws inside its own catch and the redirect below would never run
        }
      }

      // The lead is saved; show the in-funnel confirmation screen (step 16) rather than redirecting
      // out. Both non-paying lanes (below_floor and the borderline decliner) reach it; its own button
      // sends the visitor to the VIDA Select home.
      return saveAndGoToStep(FREE_DATABASE_CONFIRMATION);
    }

    // save chat state
    if (shouldSave) {
      const newState = { ...state, step: step + 1, visitedSteps: newVisitedSteps };
      const chat = await saveChatToDB(newState, ipAddress, GAId, fingerprintId);

      if (chat.leadType) setLeadType(chat.leadType); // set lead type if got one
      if (chat.adjustIncome) setState((state) => ({ ...state, adjustIncome: chat.adjustIncome })); // set adjustIncome
      setShouldSave(false); // do not save if no changes were made
    }

    // if user completed the form and it's leadType not 'Junk' - set calendly link and mark form as 'Done'
    if (step === PHONE_OR_VIDEO && preferToMeetBy && leadType !== 'Junk') {
      setState((state) => ({ ...state, calendlyLink: preferToMeetBy === 'phone' ? phoneLink : videoLink }));
      return setDone(true);
    }

    setFakeLoading(false);
    setState((state) => ({ ...state, step: state.step + 1, visitedSteps: newVisitedSteps })); // one step forward
  };

  const back = () => {
    setFakeLoading(false);
    makeFakeLoading();

    setTimeout(() => {
      setError(null); // drop error state on step back

      // skip "MatchEarn" if looking for Women or gender is Man
      if (step === CURRENT_INCOME_LEVEL && genderManOrLookingForWomen) {
        return setState((state) => ({ ...state, step: AGE_RANGE_OF_LOOKING_FOR }));
      }

      // back from "ReadyToSpeak": to the price screen when the visitor came through it, otherwise to
      // the objective question. Total by construction - only qualified and unclassified (from step
      // 8) and borderline who accepted (from step 9) can be standing at step 12; below_floor never
      // reaches it. This replaces three guards, the last of which was unreachable and whose comment
      // described a destination it did not set.
      if (step === READY_TO_SPEAK_GET_EMAIL) {
        return setState((state) => ({
          ...state,
          step: routesAsQualified(incomeLane) ? DATING_OBJECTIVE : LOW_INCOME_AGREEMENT,
        }));
      }

      // Mandatory. Without it the default 'step - 1' would send a step-15 visitor to step 14, which
      // is not in the pages array and is only rendered by a branch that requires booking links a
      // below-floor visitor never has: a blank screen.
      // Back from the free-network screen returns to wherever the visitor arrived from, which the
      // lane tells us without ambiguity: below_floor was sorted here from the income step (back to
      // the objective), borderline could only arrive by declining the price (back to that screen so
      // they can reconsider). Those are the only two lanes that can reach step 15.
      if (step === FREE_DATABASE_OPTIN) {
        return setState((state) => ({
          ...state,
          step: incomeLane === LANE_BELOW_FLOOR ? DATING_OBJECTIVE : LOW_INCOME_AGREEMENT,
        }));
      }

      // back from "Subscribe to newsletter" to "Disclaimer page"
      if (step === AGREED_TO_BUNDLE_GET_EMAIL && agreedToDisclaimer) {
        return setState((state) => ({ ...state, step: LOW_INCOME_AGREEMENT }));
      }

      setState((state) => ({ ...state, step: state.step - 1 })); // one step back
    }, fakeLoadingDuration);
  };

  return (
    <StepsContext.Provider
      value={{
        state,
        setState,
        next,
        back,
        setShouldSave,
        error,
        setError,
        stateLoading,
        fakeLoading,
        makeFakeLoading,
        fakeLoadingDuration,
        animate,
        leadType,
        phoneLink,
        videoLink,
        stepPassed,
      }}
    >
      {children}
    </StepsContext.Provider>
  );
};
